/**
 * Serviço centralizado para operações de requisição do CorelDRAW
 * @module RequestService
 */

import { corelCommunication } from '../communication';
import { debugLog, sendToTerminal } from '../../terminalLogger';
import { actionDebugger } from './actionDebugger';
import { getActionTimeout } from '../timeoutService';
import type {
  CancelRequestOptions,
  CancelRequestResponse,
  CancelRequestCallback
} from './request.types';

/**
 * Serviço centralizado para gerenciamento de requisições
 */
export class RequestService {
  /**
   * Cancela uma requisição em andamento
   * 
   * ⚠️ IMPORTANTE: 
   * - Requer requestId válido da requisição original
   * - Não há garantia de sucesso (requisição pode já ter terminado)
   * - Padrão é fire-and-forget (não aguarda confirmação)
   * 
   * @param options Opções de cancelamento ou requestId direto
   * @param callback Callback opcional para processar resposta
   * @returns Promise com resposta (se waitForResponse=true) ou void
   */
  static async cancel(
    options: CancelRequestOptions | string,
    callback?: CancelRequestCallback
  ): Promise<CancelRequestResponse | void> {
    // Normalizar opções
    const opts: CancelRequestOptions = typeof options === 'string'
      ? { requestId: options }
      : options;

    const { requestId, waitForResponse = false, timeoutMs = getActionTimeout('cancel-request') } = opts; // timeout do painel admin

    sendToTerminal('🔵 [REQUEST] Iniciando cancel', {
      requestId,
      waitForResponse,
      timeoutMs
    });

    if (!requestId) {
      const error: CancelRequestResponse = {
        status: 'error',
        message: 'requestId é obrigatório'
      };
      
      sendToTerminal('❌ [REQUEST] requestId é obrigatório');
      if (callback) callback(error);
      if (waitForResponse) return error;
      return;
    }

    if (!waitForResponse) {
      // Fire-and-forget mode (padrão)
      try {
        corelCommunication.sendActionGuarded('cancel-request', { requestId });
        
        sendToTerminal('✅ [REQUEST] cancel enviado (fire-and-forget)', {
          requestId
        });

        if (callback) {
          callback({
            status: 'ok',
            result: { sucesso: true, mensagem: 'Cancelamento enviado' }
          });
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        sendToTerminal('❌ [REQUEST] Erro em cancel', {
          requestId,
          error: errorMessage
        });

        if (callback) {
          callback({
            status: 'error',
            message: errorMessage
          });
        }
      }
      return;
    }

    // Wait for response mode
    return new Promise((resolve) => {
      const startTime = Date.now();
      const timeoutId = setTimeout(() => {
        const response: CancelRequestResponse = {
          status: 'timeout',
          message: 'Timeout ao cancelar requisição'
        };
        
        sendToTerminal('⏱️ [REQUEST] Timeout em cancel', {
          requestId,
          timeoutMs,
          elapsedMs: Date.now() - startTime
        });
        
        if (callback) callback(response);
        resolve(response);
      }, timeoutMs);

      try {
        const reqId = corelCommunication.sendActionGuarded(
          'cancel-request',
          { requestId },
          (response: any) => {
            clearTimeout(timeoutId);

            const status = response?.status || 'error';
            const result = response?.result;
            const cancelled = result?.cancelled ?? result?.sucesso ?? (status === 'ok');

            const finalResponse: CancelRequestResponse = {
              status: status as any,
              result: {
                sucesso: cancelled,
                mensagem: response?.message || result?.mensagem,
                cancelled
              },
              cancelled
            };

            sendToTerminal('✅ [REQUEST] cancel concluído', {
              requestId,
              status,
              cancelled,
              elapsedMs: Date.now() - startTime
            });

            if (callback) callback(finalResponse);
            resolve(finalResponse);
          }
        );

        if (!reqId) {
          clearTimeout(timeoutId);
          const response: CancelRequestResponse = {
            status: 'error',
            message: 'Falha ao enviar comando cancel-request'
          };
          
          sendToTerminal('❌ [REQUEST] Falha ao enviar comando cancel-request', { requestId });
          
          if (callback) callback(response);
          resolve(response);
        }
      } catch (error) {
        clearTimeout(timeoutId);
        const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
        const response: CancelRequestResponse = {
          status: 'error',
          message: errorMessage
        };
        
        sendToTerminal('❌ [REQUEST] Exceção em cancel', {
          requestId,
          error: errorMessage
        });
        
        if (callback) callback(response);
        resolve(response);
      }
    });
  }

  /**
   * Cancela requisição de forma segura (com feedback garantido)
   * 
   * @param requestId ID da requisição
   * @returns Promise<boolean> - true se cancelado ou enviado
   */
  static async cancelSafe(requestId: string): Promise<boolean> {
    sendToTerminal('🔵 [REQUEST] Iniciando cancelSafe', { requestId });
    
    try {
      const result = await this.cancel(
        { requestId, waitForResponse: true, timeoutMs: 2 * 60 * 1000 } // 2 min
      );
      
      // Considera sucesso se enviou ou se já estava concluída
      const success = result
        ? (result.status === 'ok' || result.status === 'timeout')
        : false;
      
      if (success) {
        sendToTerminal('✅ [REQUEST] cancelSafe concluído', { requestId });
      } else {
        sendToTerminal('❌ [REQUEST] cancelSafe falhou', { requestId });
      }
      
      return success;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      sendToTerminal('❌ [REQUEST] Erro em cancelSafe', {
        requestId,
        error: errorMessage
      });
      return false;
    }
  }
}

/**
 * Função auxiliar para cancelamento rápido (fire-and-forget)
 * @deprecated Use RequestService.cancel() diretamente
 */
export const cancelRequest = (requestId: string) => {
  return RequestService.cancel(requestId);
};

